import { appendable } from "@tblabs/truffle/Core/appendable";
import { StepStateDefinition } from "./StepStateDefinition";
import { StepData } from "./StepData";
import { StepType } from "./StepType";
import { HandlersCollection, Ref } from "@tblabs/truffle";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { StepEvent } from "./StepEvent";
import { Order } from "../../Models/Order";
import { Cash } from "./Cash";


export abstract class Step<S extends string>
{
    public abstract Type: StepType;
    public abstract Icon: string | (() => string);
    public abstract TitleForCustomer: () => string;
    public abstract Title: () => string;
    public abstract InitialState: S;
    public abstract WellDoneStates: S[];
    public abstract FailedDoneStates: S[];
    public abstract STATES: Record<S, StepStateDefinition>;
    public IsVisibleForCustomer: boolean = true;
    public abstract get AsEvents(): StepEvent[];
    public abstract get Mana(): Cash[];
    public abstract get WhenPlanned(): Date;
    public abstract get WhenDone(): Date;

    public Order!: Order;

    private onStepStateChange = new HandlersCollection<(data: StepData) => void>();

    constructor(public Data: StepData)
    { }

    public CallOnChange(): void
    {
        this.onStepStateChange.Call(this.Data);
    }

    public GetFullTitle(): string
    {
        return this.Icon + " " + this.Title();
    }

    public GetTitleForCustomer(): string
    {
        if (this.TitleForCustomer?.())
            return this.Icon + " " + this.TitleForCustomer?.()
        else return this.GetFullTitle();
    }

    public GetStateName(): string
    {
        return this.STATES[this.Data.State.value]?.StateName || "";
    }

    public get AllStates(): S[]
    {
        return Object.keys(this.STATES) as S[];
    }

    public OnStateChange(handler: (data: StepData) => void): void
    {
        this.onStepStateChange.Add(handler);
    }

    public get IsDone(): boolean
    {
        return this.IsWellDone || this.IsFailedDone;
    }
    public get IsWellDone(): boolean
    {
        return this.WellDoneStates.includes(this.Data.State.value as S);
    }
    public get IsFailedDone(): boolean
    {
        return this.FailedDoneStates?.includes(this.Data.State.value as S);
    }
    public get IsInInitialState(): boolean
    {
        return this.Data.State.value == this.InitialState;
    }
    public get IsInProgress(): boolean
    {
        // return (!this.IsInInitialState && !this.IsDone);
        return (!this.IsDone);
    }

    public GetAdminContent(): appendable[]
    {
        return this.STATES[this.Data.State.value]?.Admin?.Content() || [`Brak kontentu dla stanu ${this.Data.State.value || "(pusty string)"}`];
    }

    public GetCustomerContent(): appendable[]
    {
        return this.STATES[this.Data.State.value]?.Customer?.Content() || [];
    }

    public GetActions(): Record<string, () => void>
    {
        return this.STATES[this.Data.State.value]?.Admin?.Actions || {};
    }

    public GoTo(state: S): void
    {
        this.Data.State.value = state;
        this.onStepStateChange.Call(this.Data);
    }

    public get Timeout(): Date | undefined
    {
        let timeout = this.STATES[this.Data.State.value]?.Timeout?.();

        if (timeout instanceof Ref)
            timeout = timeout.value;

        return timeout;
    }

    public get HasTimeout(): boolean
    {
        return !!this.Timeout;
    }

    public get HoursToTimeout(): number | undefined
    {
        if (!this.HasTimeout)
            return undefined;

        const timeToTimeout = +(this.Timeout as Date) - +DateTimeProvider.Now;

        return timeToTimeout / (60 * 60 * 1000);
    }

    public get HoursAfterTimeout(): number | undefined
    {
        if (!this.HasTimeout)
            return undefined;

        return -this.HoursToTimeout!;
    }

    public ResetState(): void
    {
        this.Data.State.value = this.InitialState;
        this.onStepStateChange.Call(this.Data);
    }
}
